import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Storefront, CheckCircle, WarningCircle, ArrowsClockwise, Package, MagnifyingGlass, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PrintfulModule() {
  const [tab, setTab] = useState("store"); // store | catalog
  const [health, setHealth] = useState(null);
  const [storeProducts, setStoreProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const [imported, setImported] = useState(new Set()); // ids déjà importés

  async function loadHealth() {
    try {
      const r = await api.get("/printful/health");
      setHealth(r.data);
    } catch (e) {
      toast.error("Impossible de charger l'état Printful");
    }
  }

  async function loadStore() {
    setLoading(true);
    try {
      const r = await api.get("/printful/store/products");
      setStoreProducts(r.data.items || []);
    } catch (e) {
      // Ignore — boutique probablement vide
    } finally {
      setLoading(false);
    }
  }

  async function loadCatalog() {
    setLoading(true);
    try {
      const r = await api.get("/printful/catalog/products");
      setCatalogProducts(r.data.items || []);
    } catch (e) {
      toast.error("Impossible de charger le catalogue Printful");
    } finally {
      setLoading(false);
    }
  }

  async function runSync() {
    setSyncing(true);
    try {
      const r = await api.post("/printful/store/sync/now");
      toast.success(`✅ Sync terminé : ${r.data.synced} produit(s) importés`);
      await loadStore();
      await loadHealth();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Sync échoué : ${msg}`);
    } finally {
      setSyncing(false);
    }
  }

  async function importToShop(product) {
    try {
      const r = await api.post(`/printful/store/products/${product.printful_product_id}/import`);
      toast.success(`✅ Importé dans ta boutique publique : ${r.data.name}`);
      setImported(prev => new Set([...prev, product.printful_product_id]));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Import échoué : ${msg}`);
    }
  }

  useEffect(() => {
    loadHealth();
    loadStore();
    // Pre-fetch déjà importés depuis la boutique publique
    api.get("/shop/admin/products").then(r => {
      const set = new Set();
      (r.data || []).forEach(p => p.printful_sync_product_id && set.add(p.printful_sync_product_id));
      setImported(set);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "catalog" && catalogProducts.length === 0) loadCatalog();
  }, [tab]);

  const filteredCatalog = catalogProducts.filter(p =>
    !search || (p.title || "").toLowerCase().includes(search.toLowerCase()) || (p.type_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="printful-module">
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-400">
            <Storefront size={22} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Printful · Boutique Print-on-Demand</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 mt-1">Boutique Kaizen — Apparel POD</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Catalogue Printful (t-shirts, hoodies, mugs Kaizen-branded) · sync automatique vers ton store en ligne
          </p>
        </div>
        <button
          onClick={runSync}
          disabled={syncing}
          data-testid="btn-sync-printful"
          className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 text-xs font-bold uppercase tracking-widest transition flex items-center gap-2"
        >
          {syncing ? <ArrowsClockwise size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
          {syncing ? "Sync en cours…" : "Sync depuis Printful"}
        </button>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Status
          ok={health?.configured}
          label="API configurée"
          detail={health?.configured ? `Store #${health?.store?.id || "—"}` : "Token absent"}
          testid="status-config"
        />
        <Status
          ok={health?.connected}
          label="Connexion live"
          detail={health?.connected ? health?.store?.name || "Connecté" : (health?.error || "Non connecté")}
          testid="status-connect"
        />
        <Status
          ok={(health?.local_products_synced || 0) > 0}
          label="Produits boutique"
          detail={`${health?.local_products_synced || 0} produits synchronisés localement`}
          testid="status-synced"
        />
      </div>

      <nav className="flex gap-2 border-b border-zinc-800">
        <TabBtn active={tab === "store"} onClick={() => setTab("store")} testid="tab-store">
          🏪 Ma boutique ({storeProducts.length})
        </TabBtn>
        <TabBtn active={tab === "catalog"} onClick={() => setTab("catalog")} testid="tab-catalog">
          📦 Catalogue Printful (493 produits)
        </TabBtn>
      </nav>

      {tab === "catalog" && (
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher (t-shirt, hoodie, mug, gants...)"
            data-testid="catalog-search"
            className="w-full pl-10 pr-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500 outline-none"
          />
        </div>
      )}

      {loading && <div className="p-12 text-center text-zinc-500">Chargement…</div>}

      {!loading && tab === "store" && (
        <section>
          {storeProducts.length === 0 ? (
            <div className="p-8 border border-dashed border-zinc-700 rounded-lg text-center">
              <Package size={32} className="text-zinc-500 mx-auto" />
              <p className="text-zinc-300 mt-2 font-bold">Ta boutique Printful est vide</p>
              <p className="text-zinc-500 text-sm mt-1">Crée tes premiers produits Kaizen-branded depuis le catalogue.</p>
              <button
                onClick={() => setTab("catalog")}
                className="mt-4 px-4 py-2 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold uppercase tracking-widest"
              >
                Explorer le catalogue →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {storeProducts.map(p => (
                <ProductCard
                  key={p.printful_product_id}
                  product={p}
                  kind="store"
                  onImport={importToShop}
                  imported={imported.has(p.printful_product_id)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {!loading && tab === "catalog" && (
        <section>
          <div className="text-xs text-zinc-500 mb-3">
            {filteredCatalog.length} produits {search ? `correspondants à "${search}"` : "disponibles"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredCatalog.slice(0, 60).map(p => (
              <ProductCard key={p.id} product={p} kind="catalog" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Status({ ok, label, detail, testid }) {
  return (
    <div data-testid={testid} className={`p-3 rounded-lg border flex items-start gap-3 ${ok ? "bg-emerald-950/30 border-emerald-800/50" : "bg-zinc-900/60 border-zinc-800"}`}>
      {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-400 mt-0.5" /> : <WarningCircle size={18} className="text-amber-400 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-bold uppercase tracking-widest ${ok ? "text-emerald-300" : "text-zinc-300"}`}>{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{detail}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition ${active ? "text-emerald-300 border-emerald-500" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}
    >
      {children}
    </button>
  );
}

function ProductCard({ product, kind, onImport, imported }) {
  const img = product.thumbnail_url || product.image;
  const title = product.name || product.title;
  const subtitle = kind === "catalog" ? `${product.type_name || ""} · ${product.brand || ""}` : `${product.variant_count || 0} variant(s)`;
  const priceText = kind === "store" && product.variants?.[0]?.retail_price
    ? `${product.variants[0].retail_price.toFixed(2)} ${product.variants[0].currency || ""}` : null;

  return (
    <div className="p-3 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-emerald-700/50 transition group">
      <div className="aspect-square bg-zinc-950 rounded mb-2 overflow-hidden flex items-center justify-center">
        {img ? <img src={img} alt={title} className="w-full h-full object-contain group-hover:scale-105 transition" /> : <Package size={24} className="text-zinc-700" />}
      </div>
      <div className="text-xs text-zinc-100 font-medium line-clamp-2 min-h-[2.4em]">{title}</div>
      <div className="text-[10px] text-zinc-500 mt-1 truncate">{subtitle}</div>
      {priceText && (
        <div className="text-xs text-emerald-300 mt-1 flex items-center gap-1">
          <Tag size={10} /> {priceText}
        </div>
      )}
      {kind === "store" && onImport && (
        <button
          onClick={() => onImport(product)}
          data-testid={`btn-import-${product.printful_product_id}`}
          className={`mt-2 w-full px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition ${
            imported
              ? "bg-emerald-950 border border-emerald-700 text-emerald-300"
              : "bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
          }`}
        >
          {imported ? "✓ Dans la boutique" : "+ Ajouter à la boutique"}
        </button>
      )}
    </div>
  );
}
